import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    AppBar, Toolbar, Typography, Box, Button, IconButton,
    CircularProgress, Alert, Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import SelectAppStore from '../reqLab/appStore/SelectAppStore';

/**
 * Pagina admin per gestire il catalogo globale di AppVol
 * approvate per i laboratori predefiniti.
 * Riusa SelectAppStore con appvolOnly={true}.
 * Supporta sia modalità standalone (navigazione via router) che dialog (setIsShowCatalog).
 */
function ManagePredefinedAppVolCatalog({ setIsShowCatalog, baseImage, standalone }) {
    const navigate = useNavigate();
    const [repoPkgSel, setRepoPkgSel] = useState([]);
    const [, setRepoPkgBundleSel] = useState([]);
    const [isPkgListModified, setIsPkgListModified] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [statusResult, setStatusResult] = useState(null);
    const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
    const [installedPackages, setInstalledPackages] = useState([]);
    const [loading, setLoading] = useState(true);

    // Carica il catalogo corrente
    useEffect(() => {
        const fetchCatalog = async () => {
            try {
                const response = await fetch(process.env.REACT_APP_URL_GET_PREDEFINED_APPVOL_CATALOG, {
                    credentials: 'include',
                });
                const data = await response.json();
                if (data.statusRequest === 'success' && data.packages) {
                    setInstalledPackages(data.packages);
                }
            } catch (error) {
                console.error('Errore nel caricamento del catalogo:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchCatalog();
    }, []);

    const goBack = () => {
        if (standalone) {
            navigate(-1);
        } else {
            setIsShowCatalog(false);
        }
    };

    const handleBack = () => {
        if (isPkgListModified) {
            setShowUnsavedDialog(true);
        } else {
            goBack();
        }
    };

    const handleSave = async () => {
        if (isSubmitting) return;
        setIsSubmitting(true);
        setStatusResult(null);

        try {
            const response = await fetch(process.env.REACT_APP_URL_SET_PREDEFINED_APPVOL_CATALOG, {
                credentials: 'include',
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ packages: repoPkgSel }),
            });
            const data = await response.json();
            setStatusResult(data);

            if (data.statusRequest === 'success') {
                setIsPkgListModified(false);
                setInstalledPackages(repoPkgSel);
            }
        } catch (error) {
            console.error('Errore nel salvataggio:', error);
            setStatusResult({ statusRequest: 'error', message: 'Errore nel salvataggio del catalogo' });
        } finally {
            setTimeout(() => setIsSubmitting(false), 500);
        }
    };

    return (
        <React.Fragment>
            <AppBar position="sticky" color="default" elevation={1} sx={{ backgroundColor: '#fff' }}>
                <Toolbar sx={{ gap: 2 }}>
                    <IconButton edge="start" onClick={handleBack} disabled={isSubmitting}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="h6" noWrap sx={{ fontWeight: 'bold', color: '#a71e3b' }}>
                            Catalogo AppVol Predefiniti
                        </Typography>
                        <Typography variant="body2" color="textSecondary" noWrap>
                            Seleziona le applicazioni disponibili per gli studenti nei lab predefiniti
                        </Typography>
                    </Box>
                    {statusResult && (
                        <Typography
                            variant="body2"
                            color={statusResult.statusRequest === 'success' ? 'success.main' : 'error'}
                            sx={{ flexShrink: 0 }}
                        >
                            {statusResult.statusRequest === 'success'
                                ? 'Catalogo aggiornato con successo!'
                                : statusResult.message || 'Errore nell\'aggiornamento'}
                        </Typography>
                    )}
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                        disabled={isSubmitting || !isPkgListModified}
                        onClick={handleSave}
                    >
                        {isSubmitting ? 'Salvando...' : 'Salva catalogo'}
                    </Button>
                </Toolbar>
            </AppBar>

            <Box sx={{ p: 3 }}>
                <Alert severity="info" sx={{ mb: 2 }}>
                    Seleziona le applicazioni App Volumes che saranno disponibili per l'auto-assegnazione
                    da parte degli studenti nei laboratori predefiniti.
                </Alert>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress sx={{ color: '#a71e3b' }} />
                    </Box>
                ) : (
                    <SelectAppStore
                        installedPackages={installedPackages}
                        baseImage={baseImage || {}}
                        setRepoPkgSel={setRepoPkgSel}
                        setRepoPkgBundleSel={setRepoPkgBundleSel}
                        setIsPkgListModified={setIsPkgListModified}
                        appvolOnly={true}
                    />
                )}
            </Box>

            <Dialog open={showUnsavedDialog} onClose={() => setShowUnsavedDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Modifiche non salvate</DialogTitle>
                <DialogContent>
                    <Typography>
                        Hai effettuato modifiche che non sono state salvate.
                        Se esci senza salvare, le modifiche andranno perse.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowUnsavedDialog(false)} color="primary" variant="outlined">
                        Continua a modificare
                    </Button>
                    <Button onClick={() => { setShowUnsavedDialog(false); goBack(); }} color="error" variant="contained">
                        Abbandona modifiche
                    </Button>
                </DialogActions>
            </Dialog>
        </React.Fragment>
    );
}

export default ManagePredefinedAppVolCatalog;
